import { IChartConfig } from './chart-config.interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IChartDrawer } from './chart-drawer.interface';
import { DonutColorFactory } from './donut-color-factory';

export class DonutChartDrawer implements IChartDrawer {
  private readonly _totalCount: number;
  private readonly _colorFactory: DonutColorFactory;
  private readonly _data: number[];

  private _incompleteX1: number;
  private _incompleteY1: number;
  private _incompleteX2: number;
  private _incompleteY2: number;
  private _incompleteX3: number;
  private _incompleteY3: number;
  private _incompleteX4: number;
  private _incompleteY4: number;

  private _chartWidth = 500;

  private _chartHeight = 350;

  private _thickness = 50;

  private _ox: number;

  private _oy: number;

  private _innerRadius: number;

  constructor(chartConfig: IChartConfig, private readonly domSanitizer: DomSanitizer) {
    this._data = chartConfig.donutData;

    this._colorFactory = new DonutColorFactory();

    this._totalCount = this._data.reduce((sum, x) => sum + x, 0);

    this._ox = this._chartWidth / 2;

    this._oy = this._chartHeight / 2;

    this._innerRadius = this._thickness * 1.3;
  }

  public get(): SafeHtml {
    let sb = '';

    sb += `<svg viewBox="0 0 ${this._chartWidth} ${this._chartHeight}" width="100%" height="100%">`;

    sb += '<g>';

    sb += this.getSegments();

    sb += '</g>';

    sb += this.getTotalLabel();

    sb += '</svg>';

    return this.domSanitizer.bypassSecurityTrustHtml(sb);
  }

  public getIsOverflowed(): boolean {
    return false;
  }

  public getMinOffset(): number {
    return 0;
  }

  private getTotalLabel(): string {
    let sb = '';

    sb += `<g transform="translate(${this._ox}, ${this._oy + 20})">`;
    sb += `<text class="grey-text" style="font-size:22px;font-weight:500" dy="-1em" text-anchor="middle">${this._totalCount}</text>`;
    sb += '<text class="grey-text" text-anchor="middle">Total</text>';
    sb += '</g>';

    return sb;
  }

  private getSegments(): string {
    let sb = '';

    this._data.forEach((segment, index) => {
      const percent = (segment / this._totalCount) * 100;

      const path = this.getSegmentPath(index, percent);

      if (percent === 100) {
        sb += `<circle cx="${this._ox}" cy="${this._oy}" r="${
          this._innerRadius + this._thickness
        }" class="${this._colorFactory.get(index, 0, 0)}"></circle>`;
        sb += `<circle cx="${this._ox}" cy="${this._oy}" r="${this._innerRadius}" fill="white"></circle>`;
      } else {
        sb += `<path class="${this._colorFactory.get(index, 0, 0)}" d="${path}" />`;
      }
    });

    return sb;
  }

  private getSegmentPath(index: number, percent: number): string {
    switch (index) {
      case 0:
        return this.getFirstSegmentPath(percent);
      case 1:
        return this.getSecondSegmentPath(percent);
      case 2:
        return this.getThirdSegmentPath(percent);
      default:
        return '';
    }
  }

  private getFirstSegmentPath(percent: number): string {
    let sb = '';

    const angle = (2 * Math.PI * percent) / 100;
    const big = angle > Math.PI ? 1 : 0;

    const x1 = this._ox;
    const y1 = this._oy - this._innerRadius;

    sb += `M ${x1} ${y1}`;

    const x2 = this._ox;
    const y2 = y1 - this._thickness;

    sb += `V ${y2}`;

    const radius = this._innerRadius + this._thickness;
    const x3 = x2 + radius * Math.sin(angle);
    const y3 = this._oy - radius * Math.cos(angle);

    sb += `A ${radius} ${radius} 0 ${big} 1 ${x3} ${y3}`;

    const x4 = x3 - this._thickness * Math.sin(angle);
    const y4 = y3 + this._thickness * Math.cos(angle);

    sb += `L ${x4} ${y4}`;

    this._incompleteX1 = x4;
    this._incompleteY1 = y4;

    this._incompleteX2 = this._incompleteX1 + this._thickness * Math.sin(angle);
    this._incompleteY2 = this._incompleteY1 - this._thickness * Math.cos(angle);

    sb += `A ${this._innerRadius} ${this._innerRadius} 0 ${big} 0 ${x1} ${y1}`;

    return sb;
  }

  private getSecondSegmentPath(percent: number): string {
    let sb = '';

    const angle = (2 * Math.PI * percent) / 100;
    const big = angle > Math.PI ? 1 : 0;

    const x1 = this._ox;
    const y1 = this._oy - this._innerRadius;

    sb += `M ${x1} ${y1}`;

    const x2 = this._ox;
    const y2 = y1 - this._thickness;

    sb += `V ${y2}`;

    const radius = this._innerRadius + this._thickness;
    const x3 = x2 - radius * Math.sin(angle);
    const y3 = this._oy - radius * Math.cos(angle);

    sb += `A ${radius} ${radius} 0 ${big} 0 ${x3} ${y3}`;

    this._incompleteX3 = x3;
    this._incompleteY3 = y3;

    const x4 = x3 + this._thickness * Math.sin(angle);
    const y4 = y3 + this._thickness * Math.cos(angle);

    sb += `L ${x4} ${y4}`;

    this._incompleteX4 = x4;
    this._incompleteY4 = y4;

    sb += `A ${this._innerRadius} ${this._innerRadius} 0 ${big} 1 ${x1} ${y1}`;

    return sb;
  }

  private getThirdSegmentPath(percent: number): string {
    let sb = '';

    const angle = (2 * Math.PI * percent) / 100;
    const big = angle > Math.PI ? 1 : 0;

    sb += `M ${this._incompleteX1} ${this._incompleteY1}`;

    sb += `L ${this._incompleteX2} ${this._incompleteY2}`;

    const radius = this._innerRadius + this._thickness;

    sb += `A ${radius} ${radius} 0 ${big} 1 ${this._incompleteX3} ${this._incompleteY3}`;

    sb += `L ${this._incompleteX4} ${this._incompleteY4}`;

    sb += `A ${this._innerRadius} ${this._innerRadius} 0 ${big} 0 ${this._incompleteX1} ${this._incompleteY1}`;

    return sb;
  }
}
